import React from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Edit,
  CheckCircle,
  Schedule,
  Cancel,
  Delete,
} from '@mui/icons-material';
import { Appointment, AppointmentStatus } from '../types/entities';

interface AppointmentContextMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  appointment: Appointment | null;
  onEdit: (appointment: Appointment) => void;
  onDelete: (appointment: Appointment) => void;
  onStatusChange: (appointment: Appointment, newStatus: AppointmentStatus) => void;
}

const AppointmentContextMenu: React.FC<AppointmentContextMenuProps> = ({
  open,
  anchorEl,
  onClose,
  appointment,
  onEdit,
  onDelete,
  onStatusChange,
}) => {
  if (!appointment) return null;

  const handleStatusChange = (newStatus: AppointmentStatus) => {
    onStatusChange(appointment, newStatus);
    onClose();
  };

  const handleEdit = () => {
    onEdit(appointment);
    onClose();
  };

  const handleDelete = () => {
    onDelete(appointment);
    onClose();
  };

  const getStatusIcon = (status: AppointmentStatus) => {
    switch (status) {
      case AppointmentStatus.NEW:
        return <Schedule />;
      case AppointmentStatus.CONFIRMED:
        return <CheckCircle />;
      case AppointmentStatus.COMPLETED:
        return <CheckCircle />;
      case AppointmentStatus.CANCELED:
        return <Cancel />;
      default:
        return <Schedule />;
    }
  };

  const getStatusColor = (status: AppointmentStatus) => {
    switch (status) {
      case AppointmentStatus.NEW:
        return '#1976d2';
      case AppointmentStatus.CONFIRMED:
        return '#9c27b0';
      case AppointmentStatus.COMPLETED:
        return '#2e7d32';
      case AppointmentStatus.CANCELED:
        return '#d32f2f';
      default:
        return '#1976d2';
    }
  };

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          minWidth: 200,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        },
      }}
      MenuListProps={{
        sx: {
          '& .MuiMenuItem-root': {
            fontSize: '14px',
          },
        },
      }}
    >
      {/* Status Change Options */}
      <MenuItem
        onClick={() => handleStatusChange(AppointmentStatus.NEW)}
        disabled={appointment.status === AppointmentStatus.NEW}
        sx={{
          color: appointment.status === AppointmentStatus.NEW ? 'text.disabled' : '#1976d2',
        }}
      >
        <ListItemIcon sx={{ color: '#1976d2' }}>
          <Schedule />
        </ListItemIcon>
        <ListItemText primary="Mark as New" />
      </MenuItem>

      <MenuItem
        onClick={() => handleStatusChange(AppointmentStatus.CONFIRMED)}
        disabled={appointment.status === AppointmentStatus.CONFIRMED}
        sx={{
          color: appointment.status === AppointmentStatus.CONFIRMED ? 'text.disabled' : '#9c27b0',
        }}
      >
        <ListItemIcon sx={{ color: '#9c27b0' }}>
          <CheckCircle />
        </ListItemIcon>
        <ListItemText primary="Mark as Confirmed" />
      </MenuItem>

      <MenuItem
        onClick={() => handleStatusChange(AppointmentStatus.COMPLETED)}
        disabled={appointment.status === AppointmentStatus.COMPLETED}
        sx={{
          color: appointment.status === AppointmentStatus.COMPLETED ? 'text.disabled' : '#2e7d32',
        }}
      >
        <ListItemIcon sx={{ color: '#2e7d32' }}>
          <CheckCircle />
        </ListItemIcon>
        <ListItemText primary="Mark as Completed" />
      </MenuItem>

      <MenuItem
        onClick={() => handleStatusChange(AppointmentStatus.CANCELED)}
        disabled={appointment.status === AppointmentStatus.CANCELED}
        sx={{
          color: appointment.status === AppointmentStatus.CANCELED ? 'text.disabled' : '#d32f2f',
        }}
      >
        <ListItemIcon sx={{ color: '#d32f2f' }}>
          <Cancel />
        </ListItemIcon>
        <ListItemText primary="Mark as Canceled" />
      </MenuItem>

      <Divider />

      {/* Other Actions */}
      <MenuItem onClick={handleEdit}>
        <ListItemIcon>
          <Edit />
        </ListItemIcon>
        <ListItemText primary="Edit Appointment" />
      </MenuItem>

      <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
        <ListItemIcon sx={{ color: 'error.main' }}>
          <Delete />
        </ListItemIcon>
        <ListItemText primary="Delete Appointment" />
      </MenuItem>
    </Menu>
  );
};

export default AppointmentContextMenu;
